/*
	This component displays a set of FilterTags as a toolbar.

	Rhombus provides a single interface to a variety of underlying components, but it makes
	it easy to make a mess of interwoven logic setting up the configuration.

	We use a set of separate FilterTag bridges so it's simpler to understand
	what data we're passing for which filter types, and to maintain these.
*/

import React from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
	Toolbar as RhombusToolbar,
	FilterTag
} from "rhombus-react";

import { isActiveFilter } from "reducers/common/ColumnFilters";
import {
	API_DATE_FORMAT,
	DATE_FORMAT,
	TURN_OF_THE_CENTURY,
	USER_LOCALE
} from "utils/constants";

import "./FilterTagList.css";

function FilterStringTag({ filter, filterOptions, ...extraProps }){
	/*
		For a String filter, `params` is just the desired filter string

		These will display as:
			<displayName>: <value>

		And will set up a clause in an advanced filter like:
			like(columnName, *value*)
	*/
	let { displayName, params } = filter;
	let tagTitle = `${displayName}: ${params}`;

	let tagParams = {
		...extraProps,
		label: tagTitle,

		filterOptions: {
			...filterOptions,
			selected: params,
		},
	};

	return (
		<FilterTag
			{...tagParams}
		/>
	);
}

FilterStringTag.displayName = "StringTag";

function FilterCheckboxTag({ filter, filterOptions, ...extraProps }){
	/*
		For a Checkbox filter, `params` is an array of selected strings.

		These will display as:
			<displayName>: <item1>, <item2>, ...

		And will set up a clause in an advanced filter like:
			any(columnName, item1, item2, ...)
	*/
	let { displayName, params, options } = filter;
	let paramList = params.join(", ");
	let tagTitle = `${displayName}: ${paramList}`;

	/*
		Rhombus's FilterTag differs from HeaderFilter in that it doesn't process the array of string
		options that are passed to it, when it launches a CheckboxList modal, so we have to map those to
		the format it expects, which is an array of objects in the format:

		{
			label,
			name,
			value,
		}
	*/
	let checkboxItems = options?.map((option) => {
		let stringifiedOption = option.toString();
		return {
			label: stringifiedOption,
			name: stringifiedOption,
			value: stringifiedOption,
		};
	});

	let tagParams = {
		...extraProps,
		label: tagTitle,

		filterOptions: {
			...filterOptions,
			items: checkboxItems,
			selected: params,
		},
	};

	return (
		<FilterTag
			{...tagParams}
		/>
	);
}

FilterCheckboxTag.displayName = "CheckboxTag";
function FilterDateRangeTag({ Reducer, filter, filterOptions, ...extraProps }){
	/*
		For a Date filter, `params` is a 2 element array in the format: [startDate, endDate],
		where both elements are strings in YYYY-MM-DD format.

		These will display as:
			<displayName>: <startDate> - <endDate>

		And will set up a clause in an advanced filter like:
			between(columnName, startDate, endDate)
	*/
	const dispatch = useDispatch();

	let { name, displayName, params } = filter;
	let [startDate, endDate] = params;

	// We mostly want to use the API date format, but for display, we want the localized format from DATE_FORMAT
	let displayStartDate = moment(startDate).format(DATE_FORMAT);
	let displayEndDate = moment(endDate).format(DATE_FORMAT);

	let tagTitle = `${displayName}: ${displayStartDate} - ${displayEndDate}`;

	let tagParams = {
		...extraProps,
		userLocale: USER_LOCALE,
		label: tagTitle,
		filterOptions: {
			...filterOptions,
			selected: {
				startDate,
				endDate,
			},
		},

		onChange: (e) => {
			// e.values is a hash from Rhombus's `Dates` date range picker, which are `moment` objects.
			let { startDate, endDate } = e.values;

			// Use moment's `format()` method to get them in the format the API expects before putting them in the store.
			let formattedStart = startDate?.format(API_DATE_FORMAT);
			let formattedEnd = endDate?.format(API_DATE_FORMAT);

			if(!formattedStart){ formattedStart = TURN_OF_THE_CENTURY; }
			if(!formattedEnd){
				formattedEnd = moment()
					.add(1, "day")
					.format(API_DATE_FORMAT);
			}
			let params = [formattedStart, formattedEnd];

			dispatch(Reducer.updateColumnFilter(name, { params }));
		},
	};

	return (
		<FilterTag
			{...tagParams}
		/>
	);
}

FilterDateRangeTag.displayName = "DateRangeTag";

// Range filters haven't been tested on actual data yet.  There are probably Rhombus interfacing quirks to work out
function FilterRangeTag({ filter, filterOptions, ...extraProps }){
	let { displayName, params } = filter;
	let [lowBound = 0, highBound = 0] = params;
	let paramList = params.join(" - ");
	let tagTitle = `${displayName}: ${paramList}`;
	let max = Math.ceil(Math.max(...filter.options))
		, min = Math.floor(Math.min(...filter.options))
		;

	let tagParams = {
		...extraProps,
		label: tagTitle,
		items:{
			min,
			max,
		},
		filterOptions: {
			...filterOptions,
			items:{
				min,
				max,
			},
			selected: [lowBound, highBound],
		},
	};

	return (
		<FilterTag
			{...tagParams}
		/>
	);
}

FilterRangeTag.displayName = "RangeTag";

const FILTER_TAG_TYPES = {
	String: FilterStringTag,
	Checkbox: FilterCheckboxTag,
	Date: FilterDateRangeTag,
	Range: FilterRangeTag,
};

function FilterTagList({
	Reducer,
	filters,
	hideWhenEmpty = true,

	resetFilters,
	// If the filter is already represented by a different control, you can name it here to not show its tag
	excludedTagNames = [],
}){
	/*
		`Reducer` is the redux action creator bundle.  It's assumed to have the standard action creators from
		the common ColumnFilters package.

		Specifically:
			- updateColumnFilter()
			- clearColumnFilters()

		These are used for editing from modals launched from the FilterTagList, and for clearing
		filter selections.


		`filters` should be in the standard format from our common ColumnFilters redux package:
		```
			filters: {
				[columnName]: {
					type: "String" | "Range" | "Date" | "Checkbox",
					name: <columnName>,
					displayName: <string>,
					params: <variable depending on type>,
				}
			}
		```

		To be compatible with Rhombus's filter components, params will vary depending on the filter type.

		See individual filter tag components above for details on expected `params` format.
	*/
	const dispatch = useDispatch();

	let keys = Object.keys(filters);
	keys = keys.filter(name => (excludedTagNames.includes(name) === false));

	let hasActiveFilters = false; // Track this in case we want to hide the whole toolbar when it's empty

	let tagComponents = keys.map((key) => {
		let filter = filters[key];
		let { name, type } = filter;

		/*
			columnFilters will exist without `params` to store their type and naming configuration.
			If the user hasn't made a filtering selection, don't bother displaying the corresponding filter tag.
		*/
		if(!isActiveFilter(filter)){
			return null;
		}
		hasActiveFilters = true;

		let TagComponent = FILTER_TAG_TYPES[type];

		let commonTagParams = {
			/*
				`name` is used by Rhombus to modify the title bar on the launched modal.
				It currently doesn't work because of a non-overridable default `title`, but
				including `name` sets us up for the planned change (to remove the default).

				See: RRACT-64

				When completed, modal titles should render as: `${name} Column Filtering`
			*/
			name,
			Reducer,

			className: "filter-tag",

			filterType: type,
			allowRemove: true,
			allowClick: true,

			filterOptions: {
				/*
					If no value is provided for filterLevel, FilterModalLauncher errors when attempting to call its `applyFilters()` method.
					We don't use this (it's an artifact from NCM), so it's harmless to just put any value here.

					Just remember to spread `filterOptions` from the individual filter tag components to pass to `FilterTag` if you need this.
				*/
				filterLevel: "",
			},

			onChange: (e) => {
				// Generic update callback.  Different filters may need to provide a custom onChange instead.
				let params = e.values;

				dispatch(Reducer.updateColumnFilter(name, { params }));
			},

			onRemove: () => {
				dispatch(Reducer.updateColumnFilter(key, {
					params: undefined,
				}));
			},
		};

		return (
			<div
				key = {key}
			>
				<TagComponent
					{...commonTagParams}
					filter = {filter}
				/>
			</div>
		);
	});

	if(hideWhenEmpty && !hasActiveFilters){
		// Don't render anything if there are no active filters and `hideWhenEmpty` is set to `true`
		return null;
	}

	/*
	   Can provide a custom resetFilters function. E.g. tags filter needs to be cleared differently than
	   the standard clearColumnFilters
	*/
	if (!resetFilters) {
		resetFilters = () => {
			dispatch(Reducer.clearColumnFilters());
		};
	}

	return (
		<RhombusToolbar className="filter-tags-bar">
			{tagComponents}
			<a className="clear-all" onClick={resetFilters} >Clear all filters</a>
		</RhombusToolbar>
	);
}

export default FilterTagList;
